import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { DocumentOut, PageChoice } from "@/api";
import { Button } from "@/components/Button";
import { PageTitle } from "@/components/PageTitle";
import { StateMessage } from "@/components/StateMessage";
import { choiceOf, pageTiles, sameTile, tileName } from "@/lib/pageChoice";
import type { OtherPageOutcome } from "@/lib/otherPage";

import styles from "./ReadingJobScreen.module.css";
import { PagePicker } from "./PagePicker";
import { READING_DURATION, ReadingSteps } from "./ReadingSteps";

/**
 * What came of „Diese Seite lesen“ here, short of a reading under way —
 * that one leaves this view for the progress.
 *
 * - `same`: the page was read before and its reading stands (200, design
 *   2026-09-23 §3.5) — no second model call; the way on is that reading.
 * - `stand`: the Vorgang got its Stand meanwhile (409 `stand_exists`).
 * - `error`: any other refusal, in the server's own sentence.
 */
export type OtherPageResult =
  | { kind: "same"; page: number }
  | { kind: "stand" }
  | { kind: "error"; detail: string };

/**
 * „Andere Seite wählen“ (Seitenwahl, Folgepunkt a — 13.10.2026): after a
 * reading of one page failed, or did not make out its outline, every page of
 * the original again as a small picture, the page read last marked
 * „gelesen“ — in words, and **not** chosen: the next reading is a choice of
 * its own, as the first was (product rule „prüft, bestätigt nicht“).
 *
 * The same raster, the same button and the same place for a refusal as the
 * choice before the first reading (`PageChoiceStart`); only the lead says
 * what came of the page read last, and the way back leads to it.
 */
export function OtherPageChoice({
  original,
  readPage,
  outcome,
  pagePreviewUrl,
  backHref,
  drawHref,
  planningHref,
  starting,
  result,
  resultHref,
  onChoiceChange,
  onStart,
}: {
  original: DocumentOut;
  /** The page of `original` read last (`source.originalPage`). */
  readPage: number | null;
  outcome: OtherPageOutcome;
  pagePreviewUrl: (documentId: string, page: number) => string;
  /** Back to what came of the page read last: the failure, or the readings of the form. */
  backHref: string;
  drawHref: string;
  planningHref: string;
  starting: boolean;
  result: OtherPageResult | null;
  /** Where the reading of a page read before is shown (`same`). */
  resultHref: string;
  /** Another page chosen: what came of the previous one no longer applies. */
  onChoiceChange: () => void;
  onStart: (choice: PageChoice) => void;
}) {
  const [chosen, setChosen] = useState<PageChoice | null>(null);
  const readRef = useRef<HTMLButtonElement>(null);
  const toResultRef = useRef<HTMLAnchorElement>(null);
  const ids = useId();
  const titleId = `${ids}-title`;
  const statusId = `${ids}-status`;
  const resultId = `${ids}-result`;
  const tiles = pageTiles([original]);
  const chosenTile = tiles.find((tile) => sameTile(chosen, tile)) ?? null;
  const readTile = readPage !== null ? { documentId: original.id, page: readPage } : null;
  const again = chosen !== null && sameTile(readTile, chosen);
  const blocked = result?.kind === "stand";

  // „Schon gelesen“ arrives after a press: the focus goes to its way on,
  // and the link carries the sentence as its description — a status box
  // mounted with its words already in it is not reliably read out.
  useEffect(() => {
    if (result?.kind === "same") toResultRef.current?.focus();
  }, [result]);

  const status =
    chosenTile === null
      ? "Wähl zuerst eine Seite."
      : again && outcome === "unsure"
        ? `${tileName(chosenTile, false)} gewählt — schon gelesen.`
        : again
          ? `${tileName(chosenTile, false)} gewählt — wird noch einmal gelesen.`
          : `${tileName(chosenTile, false)} gewählt.`;

  return (
    <section className={`${styles.screen} ${styles.withSheet}`}>
      <PageTitle className={styles.title}>Zeichnung lesen</PageTitle>
      <div className={`${styles.layout} ${styles.choosing}`}>
        <section className={styles.pages} aria-labelledby={titleId}>
          <div className={styles.pagesHead}>
            <h2 id={titleId} className={styles.pagesTitle}>
              Welche Seite zeigt den Umriss?
            </h2>
            <p className={styles.pagesHint}>
              {original.originalFilename} hat <span className="tabular">{tiles.length}</span>{" "}
              {tiles.length === 1 ? "Seite" : "Seiten"}. Gelesen wird nur die, die du wählst.
            </p>
          </div>
          <PagePicker
            drawings={[original]}
            previewUrl={pagePreviewUrl}
            chosen={chosen}
            readTile={readTile}
            labelledBy={titleId}
            onChoose={(tile) => {
              if (!sameTile(chosen, tile)) onChoiceChange();
              setChosen(choiceOf(tile));
            }}
            onCommit={() => readRef.current?.focus()}
          />
        </section>
        <div className={styles.side}>
          <div className={styles.start}>
            <p className={styles.startLead}>
              {readPage === null
                ? outcome === "failed"
                  ? "Lesen fehlgeschlagen."
                  : "Umriss nicht sicher erkannt."
                : outcome === "failed"
                  ? `Seite ${readPage} ergab keine Lesung.`
                  : `Auf Seite ${readPage} war der Umriss nicht sicher.`}
            </p>
            <p className={styles.startText}>
              Vielleicht zeigt eine andere Seite die Skizze. Gelesen werden Umriss und Maße, danach prüfst du den
              Vorschlag. {READING_DURATION}
            </p>
            <p id={statusId} role="status" className={styles.chosenLine}>
              {status}
            </p>
            <div className={styles.ways}>
              {/* The one main action of the choice, so K60 red (design 2026-10-24
                  §8.2) — it stands under the question, apart from the notice
                  that brought it. */}
              <Button
                ref={readRef}
                variant="primary"
                busy={starting}
                aria-disabled={chosen === null || blocked ? "true" : undefined}
                aria-describedby={result !== null && result.kind !== "same" ? `${statusId} ${resultId}` : statusId}
                data-role="read-page"
                onClick={() => {
                  if (chosen !== null && !blocked) onStart(chosen);
                }}
              >
                {starting ? "Startet …" : "Diese Seite lesen"}
              </Button>
              <Link className={styles.wayLink} to={backHref}>
                Zurück
              </Link>
              <Link className={`inline-link ${styles.quietWay}`} to={drawHref}>
                Umriss selbst zeichnen
              </Link>
            </div>
            {result?.kind === "same" ? (
              <StateMessage
                id={resultId}
                live={false}
                title={`Seite ${result.page} ist schon gelesen.`}
                action={
                  <Link ref={toResultRef} className={styles.wayLink} to={resultHref} aria-describedby={resultId}>
                    Zur Lesung
                  </Link>
                }
              >
                Es gilt ihre Lesung — gelesen wurde nicht noch einmal.
              </StateMessage>
            ) : result?.kind === "stand" ? (
              <StateMessage
                id={resultId}
                tone="conflict"
                live="alert"
                title="Für diesen Vorgang gibt es schon einen Stand."
                action={
                  <Link className="inline-link" to={planningHref}>
                    Zur Planung
                  </Link>
                }
              >
                Eine Lesung würde ihn nicht ersetzen — die Seite wird deshalb nicht gelesen.
              </StateMessage>
            ) : result?.kind === "error" ? (
              <StateMessage id={resultId} tone="conflict" live="alert" title="Nicht gestartet.">
                {result.detail}
              </StateMessage>
            ) : null}
          </div>
          <ReadingSteps stage={null} status={null} />
        </div>
      </div>
    </section>
  );
}
